import { useQuery } from "@tanstack/react-query";

import {
  fetchModules,
  MODULE_MULTITENANT,
  moduleKeys,
  MODULE_INTUNE,
  MODULE_PURVIEW,
  MODULE_SECURITY_SCAN,
  MODULE_SHAREPOINT,
  MODULE_TEAMS,
  MODULE_AI_BEST_PRACTICE,
  MODULE_GROUP_DEFENDER,
  type AppModule,
} from "@/lib/api/modules";
import { fetchPlatformModules, tenantKeys } from "@/lib/api/tenants";

/**
 * Which parts of this console exist.
 *
 * The default while loading, and if the request fails, is ENABLED. That
 * direction is deliberate and matches the server: a module switch that failed
 * open would briefly show a page it should not, whereas one that failed closed
 * would make the console appear to have lost half its function every time the
 * network hiccuped. The API refuses independently, so a page shown in error
 * answers 409 rather than inventing content.
 */
export function useModules() {
  const query = useQuery({
    queryKey: moduleKeys.all(),
    queryFn: fetchModules,
    // Modules change when an administrator flips a switch, not per navigation.
    staleTime: 60_000,
  });

  return {
    modules: query.data ?? [],
    isPending: query.isPending,
    enabled: (id: string) => isEnabled(query.data, id),
    securityScan: isEnabled(query.data, MODULE_SECURITY_SCAN),
    // Intune defaults to ENABLED here while the list is loading, like every
    // other module, and the server is what actually decides. The alternative —
    // defaulting to its shipped state — would flash the navigation into
    // existence on every deployment that has it on.
    intune: isEnabled(query.data, MODULE_INTUNE),
    purview: isEnabled(query.data, MODULE_PURVIEW),
    sharepoint: isEnabled(query.data, MODULE_SHAREPOINT),
    teams: isEnabled(query.data, MODULE_TEAMS),
    aiBestPractice: isEnabled(query.data, MODULE_AI_BEST_PRACTICE),
    // The Defender menu entry exists when ANY of its five modules is in
    // effect. A heading with nothing under it advertises a part of the console
    // this deployment has decided it does not have; five separate top-level
    // entries would be five ways of saying the same thing.
    defender: anyInGroup(query.data, MODULE_GROUP_DEFENDER),
    // The individual products, for the pages and the report sections.
    defenderModules: (query.data ?? []).filter(
      (m) => m.group === MODULE_GROUP_DEFENDER && m.enabled,
    ),
  };
}

/**
 * Whether any module in a navigation group is in effect.
 *
 * While the list is loading this returns TRUE, like every other module read
 * here: a menu that flickered into existence on each navigation would be worse
 * than one briefly showing an entry whose API answers 409.
 */
function anyInGroup(mods: AppModule[] | undefined, group: string): boolean {
  if (mods === undefined) return true;
  const inGroup = mods.filter((m) => m.group === group);
  return inGroup.length === 0 || inGroup.some((m) => m.enabled);
}

function isEnabled(modules: AppModule[] | undefined, id: string): boolean {
  if (modules === undefined) return true;
  const m = modules.find((x) => x.id === id);
  return m === undefined ? true : m.enabled;
}

/**
 * Whether this deployment holds more than one tenant.
 *
 * A PLATFORM switch, read from /modules/platform — which answers whether it is
 * on or off, so the settings page can render its own control either way.
 *
 * Unlike the tenant modules above, this one defaults to OFF while the answer
 * is loading. It gates a tenant switcher and a Tenants entry, and a console
 * that flashed those into existence on every navigation would advertise a
 * shape most deployments do not have. Nothing is hidden that the operator can
 * reach anyway: the API refuses independently, and a caller who needs the
 * answer before acting (the platform index redirect) waits for `isPending`
 * rather than assuming either way.
 */
export function useMultitenant(): { enabled: boolean; isPending: boolean; isError: boolean } {
  const query = useQuery({
    queryKey: tenantKeys.platformModules(),
    queryFn: fetchPlatformModules,
    staleTime: 60_000,
    retry: false,
  });
  const row = (query.data ?? []).find((m) => m.id === MODULE_MULTITENANT);
  return {
    enabled: row?.enabled === true,
    isPending: query.isPending,
    isError: query.isError,
  };
}
